import React, { useState, useMemo } from "react";
import { useAppointments } from "../../context/AppointmentContext";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Delete02Icon,
  Calendar02Icon,
  Tick02Icon,
  Cancel01Icon,
  Search01Icon,
  Add01Icon,
} from "@hugeicons/core-free-icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CustomPagination from "../../components/pagination/CustomPagination";
import "./appointments.css";

const STATUS_LABELS = {
  pending: { label: "Pending", color: "#b45309", bg: "#fef3c7" },
  confirmed: { label: "Confirmed", color: "#166534", bg: "#dcfce7" },
  cancelled: { label: "Cancelled", color: "#991b1b", bg: "#fee2e2" },
};

// Exact repair statuses shown in the client dashboard Repair Status modal.
// The admin sets these per appointment; the client sees them verbatim.
const REPAIR_STATUSES = [
  "Appointment Booked",
  "Checked In",
  "Diagnosis & Inspection",
  "Awaiting parts/ Awaiting Approval",
  "Repair In Progress",
  "Awaiting Pickup",
  "Completed",
];

// Formats date into human readable string, e.g., "Mon, Oct 20, 2026"
const formatHumanDate = (dateVal, includeDayOfWeek = true) => {
  if (!dateVal) return "—";
  let d;
  if (typeof dateVal === "string") {
    const clean = dateVal.split("T")[0].split(" ")[0];
    const parts = clean.split("-");
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      d = new Date(year, month, day);
    } else {
      d = new Date(dateVal);
    }
  } else if (dateVal instanceof Date) {
    d = dateVal;
  } else {
    d = new Date(dateVal);
  }

  if (!d || isNaN(d.getTime())) return String(dateVal);

  return d.toLocaleDateString("en-US", {
    ...(includeDayOfWeek ? { weekday: "short" } : {}),
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Formats 24h time into readable format, e.g. "14:30" -> "2:30 PM"
const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const match = String(timeStr).match(/^(\d{1,2}):(\d{2})/);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  }
  return timeStr;
};

// Converts Date object to "YYYY-MM-DD" local string
const formatDateToISO = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const Appointments = () => {
  const {
    appointments,
    appointmentPaginationData,
    counts,
    disabledDates,
    loading,
    fetchAppointments,
    updateAppointment,
    deleteAppointment,
    addDisabledDate,
    addDisabledDates,
    removeDisabledDate,
  } = useAppointments();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tab, setTab] = useState("appointments"); // "appointments" | "dates"

  // Disabled date form
  const [selectionMode, setSelectionMode] = useState("single"); // "single" | "range"
  const [singleDate, setSingleDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [newReason, setNewReason] = useState("");
  const [dateError, setDateError] = useState("");
  const [addingDate, setAddingDate] = useState(false);

  // Delete confirm
  const [confirmDelete, setConfirmDelete] = useState(null); // appointment id

  // Extract set of YYYY-MM-DD disabled date strings to prevent duplicates and disable in calendar
  const disabledDatesSet = useMemo(() => {
    const set = new Set();
    if (Array.isArray(disabledDates)) {
      disabledDates.forEach((item) => {
        const dateStr = item?.date ? String(item.date).split("T")[0].split(" ")[0] : "";
        if (dateStr) set.add(dateStr);
      });
    }
    return set;
  }, [disabledDates]);

  // react-datepicker filterDate callback to disable already disabled dates
  const isDateSelectable = (date) => {
    const iso = formatDateToISO(date);
    return !disabledDatesSet.has(iso);
  };

  // Calculate unblocked dates in range
  const rangeDatesToBlock = useMemo(() => {
    if (!startDate) return [];
    const dates = [];
    const curr = new Date(startDate);
    curr.setHours(0, 0, 0, 0);
    const stop = endDate ? new Date(endDate) : new Date(startDate);
    stop.setHours(0, 0, 0, 0);

    while (curr <= stop) {
      const iso = formatDateToISO(curr);
      if (!disabledDatesSet.has(iso)) {
        dates.push(iso);
      }
      curr.setDate(curr.getDate() + 1);
    }
    return dates;
  }, [startDate, endDate, disabledDatesSet]);

  const currentPage = appointmentPaginationData.current_page || 1;
  const lastPage = appointmentPaginationData.last_page || 1;

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAppointments({ search, status: statusFilter === "all" ? "" : statusFilter, page: 1 });
  };

  const handlePageChange = (page) => {
    fetchAppointments({
      page,
      search,
      status: statusFilter === "all" ? "" : statusFilter,
    });
  };

  const handleStatusChange = (id, status) => {
    updateAppointment(id, { status });
  };

  const handleRepairStatusChange = (id, repairStatus) => {
    updateAppointment(id, { repairStatus });
  };

  const handleDelete = async (id) => {
    await deleteAppointment(id);
    setConfirmDelete(null);
  };

  const handleAddDate = async (e) => {
    e.preventDefault();
    setDateError("");

    if (selectionMode === "single") {
      if (!singleDate) {
        setDateError("Please select a date.");
        return;
      }
      const iso = formatDateToISO(singleDate);
      if (disabledDatesSet.has(iso)) {
        setDateError("This date is already disabled.");
        return;
      }
      setAddingDate(true);
      try {
        await addDisabledDate(iso, newReason);
        setSingleDate(null);
        setNewReason("");
      } catch (err) {
        setDateError(err.message || "Failed to disable date.");
      } finally {
        setAddingDate(false);
      }
    } else {
      if (!startDate) {
        setDateError("Please select a date or range.");
        return;
      }
      if (rangeDatesToBlock.length === 0) {
        setDateError("All dates in the selected range are already disabled.");
        return;
      }
      setAddingDate(true);
      try {
        if (rangeDatesToBlock.length === 1) {
          await addDisabledDate(rangeDatesToBlock[0], newReason);
        } else if (addDisabledDates) {
          await addDisabledDates(rangeDatesToBlock, newReason);
        } else {
          for (const d of rangeDatesToBlock) {
            await addDisabledDate(d, newReason);
          }
        }
        setStartDate(null);
        setEndDate(null);
        setNewReason("");
      } catch (err) {
        setDateError(err.message || "Failed to disable dates.");
      } finally {
        setAddingDate(false);
      }
    }
  };

  return (
    <div className="appt-page">
      {/* ── Header ── */}
      <div className="appt-header">
        <div>
          <h2 className="appt-title">Appointments</h2>
          <p className="appt-subtitle">Manage online booking requests and availability</p>
        </div>
        <div className="appt-tabs">
          <button
            className={`appt-tab-btn ${tab === "appointments" ? "active" : ""}`}
            onClick={() => setTab("appointments")}
          >
            Bookings
          </button>
          <button
            className={`appt-tab-btn ${tab === "dates" ? "active" : ""}`}
            onClick={() => setTab("dates")}
          >
            Manage Availability
          </button>
        </div>
      </div>

      {/* ── Count Cards ── */}
      <div className="appt-counts">
        {[
          { key: "total", label: "Total", color: "#3b82f6" },
          { key: "pending", label: "Pending", color: "#f59e0b" },
          { key: "confirmed", label: "Confirmed", color: "#22c55e" },
          { key: "cancelled", label: "Cancelled", color: "#ef4444" },
        ].map(({ key, label, color }) => (
          <div className="appt-count-card" key={key} style={{ borderTop: `3px solid ${color}` }}>
            <p className="appt-count-label">{label}</p>
            <p className="appt-count-value" style={{ color }}>{counts[key] ?? 0}</p>
          </div>
        ))}
      </div>

      {/* ══ TAB: APPOINTMENTS ══ */}
      {tab === "appointments" && (
        <>
          {/* ── Filters ── */}
          <form className="appt-filters" onSubmit={handleSearch}>
            <div className="appt-search-wrap">
              <HugeiconsIcon icon={Search01Icon} size={16} color="#9ca3af" />
              <input
                className="appt-search-input"
                placeholder="Search name, email, phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="appt-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                fetchAppointments({
                  status: e.target.value === "all" ? "" : e.target.value,
                  search,
                  page: 1,
                });
              }}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button type="submit" className="appt-search-btn">Search</button>
          </form>

          {/* ── Table ── */}
          <div className="appt-table-wrap">
            {loading ? (
              <div className="appt-loading">Loading…</div>
            ) : appointments.length === 0 ? (
              <div className="appt-empty">No appointments found.</div>
            ) : (
              <table className="appt-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Business</th>
                    <th>Contact</th>
                    <th>Service</th>
                    <th>Date & Time</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.map((appt) => {
                    const s = STATUS_LABELS[appt.status] || STATUS_LABELS.pending;
                    return (
                      <tr key={appt.id}>
                        <td>
                          <span className="appt-name">
                            {appt.firstName} {appt.lastName}
                          </span>
                        </td>
                        <td>{appt.businessName || <span className="appt-na">—</span>}</td>
                        <td>
                          <div>{appt.email}</div>
                          <div className="appt-phone">{appt.phone}</div>
                        </td>
                        <td>{appt.service}</td>
                        <td>
                          <div className="appt-date">{formatHumanDate(appt.date)}</div>
                          {appt.time && <div className="appt-time">{formatTime(appt.time)}</div>}
                        </td>
                        <td>
                          <span className="appt-msg">{appt.message || <span className="appt-na">—</span>}</span>
                        </td>
                        <td>
                          <span
                            className="appt-status-badge"
                            style={{ color: s.color, backgroundColor: s.bg }}
                          >
                            {s.label}
                          </span>
                          <select
                            className="appt-repair-select"
                            value={appt.repairStatus || REPAIR_STATUSES[0]}
                            onChange={(e) => handleRepairStatusChange(appt.id, e.target.value)}
                            title="Update repair status shown to the client"
                          >
                            {REPAIR_STATUSES.map((rs) => (
                              <option key={rs} value={rs}>
                                {rs}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <div className="appt-actions">
                            {appt.status !== "confirmed" && (
                              <button
                                className="appt-action-btn confirm"
                                title="Confirm"
                                onClick={() => handleStatusChange(appt.id, "confirmed")}
                              >
                                <HugeiconsIcon icon={Tick02Icon} size={15} />
                              </button>
                            )}
                            {appt.status !== "cancelled" && (
                              <button
                                className="appt-action-btn cancel"
                                title="Cancel"
                                onClick={() => handleStatusChange(appt.id, "cancelled")}
                              >
                                <HugeiconsIcon icon={Cancel01Icon} size={15} />
                              </button>
                            )}
                            <button
                              className="appt-action-btn delete"
                              title="Delete"
                              onClick={() => setConfirmDelete(appt.id)}
                            >
                              <HugeiconsIcon icon={Delete02Icon} size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Pagination ── */}
          <CustomPagination
            currentPage={currentPage}
            totalPages={lastPage}
            onPageChange={handlePageChange}
          />
        </>
      )}

      {/* ══ TAB: MANAGE DATES ══ */}
      {tab === "dates" && (
        <div className="dates-panel">
          <div className="dates-panel-left">
            <h3 className="dates-section-title">Add Unavailable Date</h3>
            <p className="dates-section-sub">
              Blocking a date prevents customers from booking on that day via the website form.
            </p>

            {/* Mode selection toggle */}
            <div className="dates-mode-toggle">
              <button
                type="button"
                className={`dates-mode-btn ${selectionMode === "single" ? "active" : ""}`}
                onClick={() => {
                  setSelectionMode("single");
                  setDateError("");
                }}
              >
                Single Date
              </button>
              <button
                type="button"
                className={`dates-mode-btn ${selectionMode === "range" ? "active" : ""}`}
                onClick={() => {
                  setSelectionMode("range");
                  setDateError("");
                }}
              >
                Date Range
              </button>
            </div>

            <form className="dates-form" onSubmit={handleAddDate}>
              <label className="dates-label">
                {selectionMode === "single" ? "Date to Block" : "Date Range to Block"}
              </label>

              {selectionMode === "single" ? (
                <DatePicker
                  selected={singleDate}
                  onChange={(date) => {
                    setSingleDate(date);
                    setDateError("");
                  }}
                  filterDate={isDateSelectable}
                  minDate={new Date()}
                  placeholderText="Click to select a date"
                  dateFormat="yyyy-MM-dd"
                  className="dates-input"
                  wrapperClassName="dates-dp-wrapper"
                  isClearable={true}
                />
              ) : (
                <>
                  <DatePicker
                    selectsRange={true}
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(update) => {
                      const [start, end] = update;
                      setStartDate(start);
                      setEndDate(end);
                      setDateError("");
                    }}
                    filterDate={isDateSelectable}
                    minDate={new Date()}
                    placeholderText="Select start date and end date"
                    dateFormat="yyyy-MM-dd"
                    className="dates-input"
                    wrapperClassName="dates-dp-wrapper"
                    isClearable={true}
                  />
                  {startDate && (
                    <div className="dates-range-summary">
                      <span>
                        {endDate
                          ? `${formatHumanDate(startDate, false)} → ${formatHumanDate(endDate, false)}`
                          : `From ${formatHumanDate(startDate, false)} (select end date)`}
                      </span>
                      {rangeDatesToBlock.length > 0 && (
                        <span className="dates-range-badge">
                          {rangeDatesToBlock.length} day{rangeDatesToBlock.length > 1 ? "s" : ""} to block
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}

              <label className="dates-label">Reason (optional)</label>
              <input
                type="text"
                className="dates-input"
                placeholder="e.g. Holiday, shop maintenance…"
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
              />
              {dateError && <p className="dates-error">{dateError}</p>}
              <button type="submit" className="dates-add-btn" disabled={addingDate}>
                <HugeiconsIcon icon={Add01Icon} size={16} />
                {addingDate
                  ? "Adding…"
                  : selectionMode === "range" && rangeDatesToBlock.length > 1
                    ? `Block ${rangeDatesToBlock.length} Dates`
                    : "Block Date"}
              </button>
            </form>
          </div>

          <div className="dates-panel-right">
            <h3 className="dates-section-title">
              Blocked Dates ({disabledDates.length})
            </h3>
            {disabledDates.length === 0 ? (
              <p className="dates-empty">No dates are currently blocked.</p>
            ) : (
              <ul className="dates-list">
                {disabledDates.map((d) => (
                  <li key={d.id} className="dates-list-item">
                    <div className="dates-list-info">
                      <HugeiconsIcon icon={Calendar02Icon} size={16} color="#6b7280" />
                      <div>
                        <span className="dates-list-date">{formatHumanDate(d.date)}</span>
                        {d.reason && <span className="dates-list-reason">{d.reason}</span>}
                      </div>
                    </div>
                    <button
                      className="dates-remove-btn"
                      onClick={() => removeDisabledDate(d.id)}
                      title="Remove block"
                    >
                      <HugeiconsIcon icon={Delete02Icon} size={15} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {confirmDelete && (
        <div className="appt-modal-overlay">
          <div className="appt-modal">
            <p className="appt-modal-title">Delete this appointment?</p>
            <p className="appt-modal-sub">This action cannot be undone.</p>
            <div className="appt-modal-actions">
              <button className="appt-modal-cancel" onClick={() => setConfirmDelete(null)}>
                Cancel
              </button>
              <button className="appt-modal-confirm" onClick={() => handleDelete(confirmDelete)}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments;
